import {
  AfterContentChecked, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef,
  EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { FormControl } from '@angular/forms';
import { debounce, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject, timer } from 'rxjs';

import { TabDefinitionModel } from 'common/src/modules/ui-components/nav-tabs/tab-definition.model';
import { FiltersGroupModel, FiltersMainComponent } from 'common/src/modules/filters';
import { DocumentPdfPreviewComponent } from '../document-pdf-preview/document-pdf-preview.component';
import { DocumentsTableComponent } from '../documents-table/documents-table.component';
import { DocumentsTableColumnModel, getDocumentsTableColumnsGrid } from '../documents-table/documents-table-column.model';
import { ColumnsSettingsComponent } from '../columns-settings/columns-settings.component';

// Keep in sync with $dc-preview-min-width / $dc-preview-max-width / $dc-preview-basis in common/src/styles/_variables.scss
const PREVIEW_MIN_WIDTH = 620;
const PREVIEW_MAX_WIDTH = 1120;
const PREVIEW_BASIS_RATIO = 0.33;

export type SearchPlaceholderConfig<T extends string = string> = string | ({ default?: string } & Partial<Record<T, string>>);

@Component({
  selector: 'rnpl-documents-container',
  templateUrl: './documents-container.component.html',
  styleUrls: ['./documents-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideUpDown', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('200ms ease-out', style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ transform: 'translateY(100%)', opacity: 0 })),
      ]),
    ]),
  ]
})
export class DocumentsContainerComponent implements OnInit, OnChanges, AfterContentChecked, AfterViewInit, OnDestroy {

  public searchControl = new FormControl('');

  @Input() public tabs: TabDefinitionModel[] = [];
  @Input() public filtersConfig: FiltersGroupModel[] = [];
  @Input() public activeStatus: string;
  @Input() public mainColor: string;
  @Input() public title: string;
  @Input() public searchPlaceholder: SearchPlaceholderConfig;
  @Input() public hideSearch = false;
  @Input() public hideSettingsBar = false;
  // for lists embedded into a page that already has its own head (e.g. a profile sub-page)
  @Input() public hideHead = false;
  @Input() public hasSidePanel = false;
  @Input() public icon: string;
  @Input() public isLoading: boolean;
  @Input() public isEmpty: boolean;
  @Input() public updatedAt: Date;
  @Input() public selectedRows: any[] = [];
  @Input() public columns: DocumentsTableColumnModel[] = [];
  @Input() public selectable: boolean;
  @Input() public resetPreviewOnTabChange = false;

  @Output() syncRefreshEmit = new EventEmitter<null>();
  @Output() syncClickEmit = new EventEmitter<null>();
  @Output() searchEmit = new EventEmitter<string>();
  @Output() filtersAppliedEmit = new EventEmitter<string>();

  @ContentChild(DocumentPdfPreviewComponent, { static: false }) public previewRef: DocumentPdfPreviewComponent;
  @ContentChild(DocumentsTableComponent, { static: false }) private tableRef: DocumentsTableComponent;
  @ContentChild(DocumentPdfPreviewComponent, { read: ElementRef, static: false }) private previewElementRef: ElementRef<HTMLElement>;
  @ContentChild(ColumnsSettingsComponent, { static: false }) private columnsSettingsRef: ColumnsSettingsComponent;

  @ViewChild('dcBody', { static: true }) private dcBodyRef: ElementRef<HTMLElement>;
  @ViewChild('naturalWidthProbe', { static: true }) private naturalWidthProbeRef: ElementRef<HTMLElement>;
  @ViewChild('filters', { static: false }) public filters: FiltersMainComponent;

  public isSyncing = false;

  // without search the filter button sits next to the tabs
  public get filtersNextToTabs(): boolean {
    return this.hideSearch && !!(this.tabs && this.tabs.length) && !!(this.filtersConfig && this.filtersConfig.length);
  }

  private search$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  private resizeObserver: any;
  private naturalWidthObserver: any;
  private hasEnoughSpace = true;
  private viewInitialized = false;

  private userPreviewChoice: boolean | null = null;

  private tableNaturalWidth = 0;

  private tableWidthReady = false;
  private spaceCheckTimeout: any;
  private spaceCheckBurstStartedAt: number = null;
  private initialPreviewDecisionApplied = false;
  private probeColumns: DocumentsTableColumnModel[] = null;
  private probeSelectable: boolean = null;
  private columnsSettingsSubscribed = false;
  private columnsSettingsLoadingStarted = false;
  private columnsSettingsResolved = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeStatus && !changes.activeStatus.firstChange
      && changes.activeStatus.previousValue !== changes.activeStatus.currentValue) {
      this.userPreviewChoice = null;
      if (this.resetPreviewOnTabChange) {
        this.resetPreviewDecision();
      }
      this.checkPreviewSpace();
    }

    if (changes.isLoading && !changes.isLoading.firstChange
      && changes.isLoading.previousValue && !changes.isLoading.currentValue) {
      this.checkPreviewSpace();
    }

    if (changes.updatedAt) {
      this.isSyncing = false;
    }
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        debounce(value => timer(value ? 300 : 0)),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => this.searchEmit.emit(value));
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.initPreviewSpaceObservers();
  }

  ngAfterContentChecked(): void {
    this.syncNaturalWidthProbe();
    this.subscribeToColumnsSettings();

    if (this.viewInitialized && this.previewRef && !this.resizeObserver) {
      this.initPreviewSpaceObservers();
    }
  }

  private resetPreviewDecision(): void {
    this.initialPreviewDecisionApplied = false;
    this.columnsSettingsResolved = false;
    this.columnsSettingsLoadingStarted = !!this.columnsSettingsRef && this.columnsSettingsRef.loading$.getValue();
  }

  private subscribeToColumnsSettings(): void {
    if (this.columnsSettingsSubscribed || !this.columnsSettingsRef) { return; }
    this.columnsSettingsSubscribed = true;

    this.columnsSettingsRef.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading: boolean) => {
        if (loading) {
          this.columnsSettingsLoadingStarted = true;
          return;
        }
        if (!this.columnsSettingsLoadingStarted || this.columnsSettingsResolved) { return; }

        // the page applies the emitted columns in the same change detection cycle; measure after it
        setTimeout(() => {
          this.columnsSettingsResolved = true;
          this.tableNaturalWidth = Math.ceil(this.naturalWidthProbeRef.nativeElement.getBoundingClientRect().width);
          this.applyPreviewSpaceDecision();
          this.cdr.markForCheck();
        });
      });
  }

  private get isTableWidthFinal(): boolean {
    return !this.columnsSettingsRef || this.columnsSettingsResolved;
  }

  public get showSkeleton(): boolean {
    return this.isLoading || (!!this.previewRef && !this.isTableWidthFinal);
  }

  private syncNaturalWidthProbe(): void {
    const columns = this.tableRef ? this.tableRef._columns : null;
    const selectable = this.tableRef ? !!this.tableRef.selectable : false;
    if (columns === this.probeColumns && selectable === this.probeSelectable) { return; }

    this.probeColumns = columns;
    this.probeSelectable = selectable;
    this.naturalWidthProbeRef.nativeElement.style.gridTemplateColumns = columns
      ? getDocumentsTableColumnsGrid(columns, selectable)
      : '';
  }

  private initPreviewSpaceObservers(): void {
    if (!this.previewRef || this.resizeObserver || !(window as any).ResizeObserver) { return; }

    this.resizeObserver = new (window as any).ResizeObserver(() => this.onPreviewSpaceObserved());
    this.resizeObserver.observe(this.dcBodyRef.nativeElement);

    if (this.tableRef) {
      // the table width changes in one step (columns config), so it is applied without the resize debounce
      this.naturalWidthObserver = new (window as any).ResizeObserver((entries: any[]) => {
        this.tableNaturalWidth = Math.ceil(entries[0].contentRect.width);
        this.tableWidthReady = true;
        this.applyPreviewSpaceDecision();
      });
      this.naturalWidthObserver.observe(this.naturalWidthProbeRef.nativeElement);
    } else {
      this.tableWidthReady = true;
    }
  }

  private onPreviewSpaceObserved(): void {
    if (this.initialPreviewDecisionApplied) {
      this.checkPreviewSpace();
      return;
    }
    this.applyPreviewSpaceDecision();
  }

  public get previewForcedOpen(): boolean {
    return !!this.previewRef && this.previewRef.expanded && !this.hasEnoughSpace;
  }

  public get effectiveSearchPlaceholder(): string {
    if (!this.searchPlaceholder) { return null; }
    if (typeof this.searchPlaceholder === 'string') { return this.searchPlaceholder; }
    return this.searchPlaceholder[this.activeStatus] || this.searchPlaceholder.default;
  }

  public onPreviewToggleClick(): void {
    if (!this.previewRef) { return; }
    this.previewRef.togglePreview();
    this.userPreviewChoice = this.previewRef.expanded;
    this.cdr.detectChanges();
  }

  private checkPreviewSpace(): void {
    const now = Date.now();
    if (this.spaceCheckBurstStartedAt === null) {
      this.spaceCheckBurstStartedAt = now;
    }
    if (this.spaceCheckTimeout) {
      clearTimeout(this.spaceCheckTimeout);
    }
    const elapsed = now - this.spaceCheckBurstStartedAt;
    const delay = elapsed >= 150 ? 0 : 150 - elapsed;
    this.spaceCheckTimeout = setTimeout(() => {
      this.spaceCheckTimeout = null;
      this.spaceCheckBurstStartedAt = null;
      this.applyPreviewSpaceDecision();
    }, delay);
  }

  private applyPreviewSpaceDecision(): void {
    if (!this.previewRef) { return; }
    if (!this.tableWidthReady) { return; }

    if (!this.isTableWidthFinal) { return; }

    const animate = this.initialPreviewDecisionApplied;
    this.initialPreviewDecisionApplied = true;

    const tableWidth = this.tableNaturalWidth;
    const availableWidth = this.dcBodyRef.nativeElement.clientWidth;
    const previewWidth = Math.min(PREVIEW_MAX_WIDTH, Math.max(PREVIEW_MIN_WIDTH, availableWidth * PREVIEW_BASIS_RATIO));
    const hasEnoughSpace = this.hasEnoughSpace
      ? availableWidth >= tableWidth + previewWidth
      : availableWidth >= tableWidth + previewWidth;

    const spaceChanged = hasEnoughSpace !== this.hasEnoughSpace;
    this.hasEnoughSpace = hasEnoughSpace;

    if (this.userPreviewChoice !== null) {
      if (spaceChanged) { this.cdr.detectChanges(); }
      return;
    }

    if (this.previewRef.expanded && !hasEnoughSpace) {
      this.setPreviewExpanded(false, animate);
      this.cdr.detectChanges();
    } else if (!this.previewRef.expanded && hasEnoughSpace) {
      this.setPreviewExpanded(true, animate);
      this.cdr.detectChanges();
    } else if (spaceChanged) {
      this.cdr.detectChanges();
    }
  }

  // The preview host bindings live in the page view (often OnPush) and the ResizeObserver callbacks run outside
  // the Angular zone, so the page is marked for check and an app-wide check is run only when the preview really toggles
  private setPreviewExpanded(expanded: boolean, animate: boolean): void {
    this.ngZone.run(() => {
      const host = this.previewElementRef ? this.previewElementRef.nativeElement : null;
      if (!animate && host) {
        host.style.transition = 'none';
        requestAnimationFrame(() => requestAnimationFrame(() => host.style.transition = ''));
      }

      this.previewRef.setExpanded(expanded);
      this.cdr.markForCheck();
    });
  }

  public clearSearch(): void {
    if (!this.searchControl.value) { return; }
    this.searchControl.patchValue(null);
  }

  public syncRefresh(): void {
    if (this.isSyncing || this.isLoading) { return; }
    if (this.updatedAt) {
      this.isSyncing = true;
    }
    this.syncRefreshEmit.emit();
  }

  public syncClick(): void {
    this.syncRefresh();
  }

  public filtersApplied(): void {
    this.filtersAppliedEmit.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.naturalWidthObserver) {
      this.naturalWidthObserver.disconnect();
    }
  }

}
